<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { DgrRadio, RadioItem } from '@stockmarkteam/donguri-ui';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Content from '@/components/layouts/content.vue';
import Header from '@/components/layouts/header.vue';
import { FeedType } from '@/types';

const store = useStore();
const { createSnackbar } = useSnackbar();
const feedType = computed<FeedType>(() => store.state.feedType.feedType);

const radioOptions: RadioItem<FeedType>[] = [
  { value: 'all', label: '国内・海外メディア' },
  { value: 'domestic', label: '国内メディアのみ' },
  { value: 'foreign', label: '海外メディアのみ' },
];

const selectedOption = ref<FeedType>(feedType.value);

const submit = async () => {
  if (feedType.value === selectedOption.value) return;

  await store.dispatch('feedType/updateFeedType', selectedOption.value);
  createSnackbar({
    message: 'メディアの表示設定を更新しました',
    type: 'success',
  });
};
</script>
<template>
  <div class="media-display-settings" v-if="feedType">
    <Header title="メディアの表示"></Header>
    <Content>
      <div class="c-title-medium--m lead">
        フィードに表示するニュースメディアの範囲を選べます。
      </div>
      <div class="c-text-regular--s note">
        ※「メンバーの活動状況」「注目情報ランキング」は、この設定にかかわらず国内・海外メディアの両方が表示されます。
      </div>
      <div class="settings-card">
        <div class="options">
          <DgrRadio
            :items="radioOptions"
            v-model="selectedOption"
            orientation="vertical"
            :vertical-layout="true"
            class="c-text-regular--m"
          />
          <div
            v-if="selectedOption === 'foreign'"
            class="c-text-regular--s message"
          >
            <div>
              「業界・他社の動き」は現在、国内メディアのみ対応しています。
            </div>
            <div>海外メディアのみを選択すると、記事は表示されません。</div>
          </div>
        </div>
      </div>
    </Content>
    <div class="settings-footer">
      <button
        class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
        @click="submit"
      >
        保存
      </button>
    </div>
  </div>
</template>
<style lang="scss" scoped>
// 内容が短くても画面の最下部に置く
.settings-footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 16px 24px;
  background: $color-white;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
}

.media-display-settings {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  // コンテンツを内容領域いっぱいに広げる（Header・Contentの幅もこの変数で決まる）
  --layout-content-width: 100%;

  .lead {
    margin: 0 24px;
  }

  .note {
    color: $color-gray800;
    margin: 8px 24px 12px;
  }

  .settings-card {
    box-sizing: border-box;
    max-width: 1248px;
    margin: 0 24px 24px;
    padding: 16px;
    background: #fff;
    border: 1px solid $color-border;
    border-radius: $border-radius;

    .options {
      display: flex;
      gap: 16px;

      .message {
        display: flex;
        flex-direction: column;
        height: 40px;
        align-self: flex-end;
        justify-content: center;
      }
    }

    :deep(.item) label {
      box-sizing: border-box;
      width: 100%;
      height: 40px;
      gap: 8px;
      padding: 8px 16px;
      border: 1px solid $color-gray400;
      border-radius: $border-radius;

      &:hover {
        background: $color-gray400;
      }

      &[data-state='checked'] {
        border: 1px solid $color-green100;
        background: $color-green100;

        .c-inputLabel {
          color: $color-green600;
        }

        .icon-box {
          color: $color-green600;
        }
      }
    }
  }
}
</style>
